/**
 * EdgeSyncService
 *
 * E018-S009: Reconciliation service that syncs required edges with actual edges.
 * Creates/deletes edges to match the derived state.
 */

import type { IEdgeRenderingService } from '../core/interfaces/IEdgeRenderingService';
import type { IEdgeSyncService } from '../core/interfaces/IEdgeSyncService';
import type { EdgeDefinition, EdgeSyncResult } from '../core/types';

// =============================================================================
// Implementation
// =============================================================================

/**
 * Service that synchronizes edges with required edge definitions.
 *
 * Uses RECONCILIATION pattern:
 * - Compare required vs existing
 * - Add missing edges
 * - Remove obsolete edges
 */
export class EdgeSyncService implements IEdgeSyncService {
  /** Set of currently existing edge IDs */
  private readonly existingEdgeIds = new Set<string>();

  constructor(private readonly renderingService: IEdgeRenderingService) {}

  /**
   * Synchronizes edges with required definitions.
   */
  sync(required: EdgeDefinition[]): EdgeSyncResult {
    const requiredIds = new Set(required.map(e => e.id));
    const added: string[] = [];
    const removed: string[] = [];

    // Remove obsolete edges
    for (const id of this.existingEdgeIds) {
      if (!requiredIds.has(id)) {
        this.renderingService.removeEdge(id);
        this.existingEdgeIds.delete(id);
        removed.push(id);
      }
    }

    // Add missing edges
    for (const def of required) {
      if (!this.existingEdgeIds.has(def.id)) {
        this.renderingService.addEdge(def);
        this.existingEdgeIds.add(def.id);
        added.push(def.id);
      }
    }

    return {
      added,
      removed,
      unchanged: required.length - added.length,
    };
  }

  /**
   * Gets existing edge IDs.
   */
  getExistingEdgeIds(): string[] {
    return Array.from(this.existingEdgeIds);
  }

  /**
   * Checks if edge exists.
   */
  hasEdge(id: string): boolean {
    return this.existingEdgeIds.has(id);
  }

  /**
   * Disposes the service.
   */
  dispose(): void {
    // Remove all edges
    for (const id of this.existingEdgeIds) {
      this.renderingService.removeEdge(id);
    }
    this.existingEdgeIds.clear();
  }
}
